// https://www.dbos.dev/opn-pledge

import { SiteLayout } from '@components/layout/SiteLayout';
import { FooterCtaBuildYourReliableBackend } from '@components/layout/FooterCtas';

export function OpnPledgePage() {
  return (
    <SiteLayout banner={true} footerCta={<FooterCtaBuildYourReliableBackend />}>
      <section className="hero-title">
        <h1 className="text-heading-h1">DBOS Open Patent Non-Assertion Pledge</h1>
        <p className="text-body text-alignment-left">Last updated: August 2026</p>
      </section>
      <section className="legal-wrapper">
        <div className="text-body text-body-thin w-richtext">
          <p>
            DBOS, Inc. (“DBOS”) is committed to promoting innovation to further the overall growth and advancement of
            information technology and believes that Free or Open Source Software is a very important tool for fostering
            innovation. Inspired by Google's OPN Pledge (
            <a href="https://www.google.com/patents/opnpledge/pledge/">
              https://www.google.com/patents/opnpledge/pledge/
            </a>
            ), DBOS is therefore pledging the free use of the patent identified below in connection with Free or Open
            Source Software on the following terms:
          </p>
          <p>{'\u200d'}</p>
          <p>
            <strong>{'1. \u00a0DEFINITIONS.'}</strong>
          </p>
          <p>
            {
              '“Free or Open Source Software” means any software that is licensed or otherwise distributed to the public in such a way that satisfies any version of “The Open Source Definition” provided by the Open Source Initiative at '
            }
            <a href="https://opensource.org/osd">opensource.org/osd</a>
            {' or any version of “The Free Software Definition” provided by the Free Software Foundation at '}
            <a href="https://www.gnu.org/philosophy/free-sw.html">gnu.org/philosophy/free-sw.html</a>.
          </p>
          <p>
            {'“Pledged Patent” means '}
            <a href="/assets/images/6a70c1bb2aef4d292e587247_US-12625863-B1.pdf">U.S. Patent No. 12,625,863 B1</a>,
            including any reissue, reexamination certificate, or patent-term extension thereof.
          </p>
          <p>
            “Pledge” means DBOS’s non-assertion promise and transfer commitment set forth under “Our Pledge”, subject to
            the limitations and Defensive Termination provisions stated herein.
          </p>
          <p>{'\u200d'}</p>
          <p>
            <strong>{'2. \u00a0OUR PLEDGE.'}</strong>
          </p>
          <p>
            DBOS promises to each person or entity that develops, distributes or uses Free or Open Source Software (a
            “Pledge Recipient”) that DBOS will not bring a lawsuit or other legal proceeding against a Pledge Recipient
            for patent infringement under the Pledged Patent based on the Pledge Recipient’s (i) development,
            manufacture, use, sale, offer for sale, lease, license, exportation, importation or distribution of any Free
            or Open Source Software, or (ii) internal-only use of Free or Open Source Software, either as obtained by
            Pledge Recipient or as modified by Pledge Recipient, in standalone form or combined with hardware or with
            any other software (“Internal-Only Use”). The preceding Pledge does not apply to any infringement of the
            Pledged Patent by hardware or by software that is not Free or Open Source Software, or by Free or Open
            Source Software combined with special purpose hardware or with software that is not Free or Open Source
            Software (except Internal-Only Use).
          </p>
          <p>
            It is DBOS’s intent that the Pledge be legally binding, irrevocable (except as otherwise provided under
            “Defensive Termination” below) and enforceable against DBOS and entities controlled by DBOS, and their
            successors and assigns. Thus, DBOS will require any person or entity to whom it sells or transfers the
            Pledged Patent to agree, in writing, to abide by the Pledge and to impose the same requirement on any
            subsequent transferee.
          </p>
          <p>
            The Pledge is not an assurance that the Pledged Patent covers any particular software or hardware or is
            enforceable, that the Pledged Patent is the only patent that does or may cover any particular Free or Open
            Source Software, that any activities covered by the Pledge will not infringe patents or other intellectual
            property rights of a third party, or that DBOS will pledge any other patents. Except as expressly stated in
            the Pledge, no other rights are waived or granted by DBOS or received by a Pledge Recipient, whether by
            implication, estoppel, or otherwise.
          </p>
          <p>{'\u200d'}</p>
          <p>
            <strong>{'3. \u00a0DEFENSIVE TERMINATION.'}</strong>
          </p>
          <p>
            Because our Pledge is a promise not to assert the Pledged Patent without requiring any payment from a Pledge
            Recipient, we think it is only fair that we condition the Pledge upon the Pledge Recipient (and its
            affiliates) not asserting or profiting from the assertion of patents against DBOS, its affiliates, or its
            products or services. Accordingly, DBOS reserves the right to terminate the Pledge, to the extent DBOS deems
            necessary to protect itself, its affiliates, or its products and services (“Defensive Termination”) with
            respect to any Pledge Recipient (or affiliate) who files a lawsuit or other legal proceeding for patent
            infringement or who has a direct financial interest in such lawsuit or other legal proceeding (an “Asserting
            Party”) against DBOS or any entity controlled by DBOS or against any third party based in whole or in part
            on any product or service developed by or on behalf of DBOS or any entity controlled by DBOS.
          </p>
          <p>
            Any Defensive Termination by DBOS with respect to an Asserting Party shall have the same effect as if Our
            Pledge had never been extended to such Asserting Party in the first instance. DBOS, in its sole discretion,
            shall determine the manner and terms, if any, by which rights under the Pledged Patent may be extended to an
            Asserting Party after that Asserting Party’s lawsuit or other legal proceeding is permanently dismissed,
            terminated or withdrawn in writing.
          </p>
          <p>{'\u200d'}</p>
          <p>
            <strong>{'4. \u00a0MISTAKEN ASSERTION.'}</strong>
          </p>
          <p>
            Should DBOS ever initiate a lawsuit or other legal proceeding for patent infringement based on software
            which is not the subject of a Defensive Termination, and then receive written notice from the party against
            whom such lawsuit or other legal proceeding has been filed providing sufficient information for DBOS to
            reasonably determine that such software in fact satisfies the requirements of the Pledge, then DBOS will use
            reasonable efforts to withdraw such lawsuit (or the applicable claims therein) or move to terminate such
            other legal proceeding (or the applicable portions thereof) within sixty (60) days after receiving such
            written notice.
          </p>
          <p>{'\u200d'}</p>
          <p>
            <em>{'Adapted from Google’s '}</em>
            <a href="https://www.google.com/patents/opnpledge/pledge/">
              <em>Open Patent Non-Assertion Pledge</em>
            </a>
            <em>{', licensed under the '}</em>
            <a href="https://creativecommons.org/licenses/by/3.0/">
              <em>Creative Commons Attribution 3.0 Unported License</em>
            </a>
            <em>. DBOS has modified the original text. Google does not endorse DBOS or this pledge.</em>
          </p>
        </div>
      </section>
    </SiteLayout>
  );
}
